import { BoxGeometry, ConeGeometry, CylinderGeometry, Group, Mesh, SphereGeometry, Vector3, type BufferGeometry, type Material } from "three";
import { creatureLook } from "@eldrun/content";

/**
 * Peças dos bonecos procedurais das criaturas (ADR 0007), sem material próprio: só three e o conteúdo.
 * O cliente monta o boneco com o toon (`creature-model.ts`); o build das criaturas animadas (pele
 * rígida, ADR 0012) monta as mesmas peças e prende cada uma a um osso do molde humano, então o modelo
 * animado do Troll, do Ciclope e dos Guardiões é o boneco de hoje, articulado.
 */

/**
 * Geometrias compartilhadas por chave: criaturas surgem e somem o tempo todo, e cada corpo novo
 * reaproveita as mesmas (nada a liberar quando uma criatura sai de cena).
 */
const geometries = new Map<string, BufferGeometry>();
function shared(key: string, make: () => BufferGeometry): BufferGeometry {
  let g = geometries.get(key);
  if (!g) geometries.set(key, (g = make()));
  return g;
}
const box = (w: number, h: number, d: number) => shared(`b${w},${h},${d}`, () => new BoxGeometry(w, h, d));
const cone = (r: number, h: number, seg: number) => shared(`c${r},${h},${seg}`, () => new ConeGeometry(r, h, seg));
const sphere = (r: number, w: number, h: number) => shared(`s${r},${w},${h}`, () => new SphereGeometry(r, w, h));
const cylinder = (top: number, bottom: number, h: number, seg: number, start = 0, arc = Math.PI * 2) =>
  shared(`y${top},${bottom},${h},${seg},${start},${arc}`, () => new CylinderGeometry(top, bottom, h, seg, 1, false, start, arc));

function mesh(geometry: BufferGeometry, material: Material, x = 0, y = 0, z = 0, rx = 0, rz = 0): Mesh {
  const m = new Mesh(geometry, material);
  m.position.set(x, y, z);
  m.rotation.set(rx, 0, rz);
  return m;
}

const UP = new Vector3(0, 1, 0);

function joint(x: number, y: number, z: number, ...children: Mesh[]): Group {
  const g = new Group();
  g.position.set(x, y, z);
  if (children.length > 0) g.add(...children);
  return g;
}

/** Quatro patas penduradas nos quadris. */
function fourLegs(material: Material, halfX: number, frontZ: number, backZ: number, hip: number, size: number): Group[] {
  const leg = box(size, hip, size);
  return [
    [-halfX, frontZ],
    [halfX, frontZ],
    [-halfX, backZ],
    [halfX, backZ],
  ].map(([x, z]) => joint(x as number, hip, z as number, mesh(leg, material, 0, -hip / 2, 0)));
}

/** Duas pernas (bípedes) penduradas nos quadris. */
function twoLegs(material: Material, halfX: number, hip: number, size: number, z = 0): Group[] {
  const leg = box(size, hip, size);
  return [-halfX, halfX].map((x) => joint(x, hip, z, mesh(leg, material, 0, -hip / 2, 0)));
}

/** Dois braços pendurados nos ombros. */
function twoArms(material: Material, halfX: number, shoulder: number, length: number, size: number, z = 0): Group[] {
  const arm = box(size, length, size);
  return [-halfX, halfX].map((x) => joint(x, shoulder, z, mesh(arm, material, 0, -length / 2, 0)));
}

/** Cinco pontas de coroa em anel (raio `r`, centro em y e z), a primeira à frente (−z). */
function crownPoints(material: Material, r: number, y: number, z = 0): Mesh[] {
  const point = cone(0.045, 0.14, 4);
  return Array.from({ length: 5 }, (_, i) => {
    const a = (i / 5) * Math.PI * 2;
    return mesh(point, material, Math.sin(a) * r, y, z - Math.cos(a) * r);
  });
}

/** Osso dos esqueletos KayKit (a média da célula do osso no atlas): o mesmo tom no boneco e no animado (F50). */
const SKELETON_BONE = "#CFB299";

/** A cor de uma célula recolorida no visual animado (F50): o boneco procedural usa a mesma. */
function lookHex(type: string, cell: number, fallback: string): string {
  return creatureLook(type)?.tint?.find((t) => t.cells?.includes(cell))?.hex ?? fallback;
}

/**
 * Material da parte, pela cor (`#RRGGBB`), o contorno de luz e o brilho próprio: o cliente dá o toon
 * (a reforçada com contorno bem mais forte); o build das criaturas animadas, só os dados.
 */
export type Paint = (hex: string, rim?: number, glow?: boolean) => Material;

/** As partes articuladas de um boneco (pivôs nos quadris, ombros e pescoço), sem a raiz. */
export interface CreatureParts {
  /** Corpo (sobe e desce, inclina, enrola). */
  body: Group;
  head: Group;
  legs: Group[];
  /** Braços (assombrações de pé), pendurados nos ombros. */
  arms?: Group[];
  /** Asas (voadoras), presas nas costas. */
  wings?: Group[];
  /** Comprimento das pernas (amplitude da passada). */
  legLength: number;
}

type Builder = (paint: Paint) => CreatureParts;

/** Cores do Troll de antes. */
const TROLL: TrollColors = { fur: "#4D3B2E", dark: "#2E231B", face: "#8C735F", tusk: "#E8DCC0", eye: "#FF5A3C" };
/** Pedra dos Guardiões e do Troll de Pedra (F89). */
const STONE = "#7C7A72";
const STONE_DARK = "#4E4C46";

/**
 * Criaturas low-poly procedurais (ADR 0007), com a frente para −z (como o boneco) e os pés em
 * y = 0. Fauna em terra, ocre e cinza (doc 05); assombrações em tons secos e frios, com olhos e
 * fogo com brilho próprio, que leem bem à noite.
 */
export const CREATURE_SHAPES: Readonly<Record<string, Builder>> = {
  capivara: (paint) => {
    // Cervo: pernas longas, pescoço erguido, rabo branco e galhada pequena.
    const fur = paint("#8A5A3C", 0.7);
    const dark = paint("#5C3A26", 0.6);
    const pale = paint("#E8DCC0", 0.6);
    const antler = paint("#D9C9A8", 0.6);
    const body = joint(0, 0, 0, mesh(box(0.32, 0.3, 0.78), fur, 0, 0.54, 0), mesh(box(0.1, 0.12, 0.04), pale, 0, 0.62, 0.4));
    const head = joint(0, 0.62, -0.36, mesh(box(0.12, 0.26, 0.12), fur, 0, 0.08, -0.05, -0.5), mesh(box(0.15, 0.15, 0.24), fur, 0, 0.21, -0.17), mesh(box(0.1, 0.09, 0.1), dark, 0, 0.18, -0.32));
    const ear = box(0.1, 0.04, 0.03);
    const beam = cone(0.022, 0.12, 4);
    const tine = cone(0.016, 0.07, 4);
    head.add(
      mesh(ear, fur, -0.12, 0.27, -0.09),
      mesh(ear, fur, 0.12, 0.27, -0.09),
      mesh(beam, antler, -0.05, 0.33, -0.1, 0.25, 0.35),
      mesh(beam, antler, 0.05, 0.33, -0.1, 0.25, -0.35),
      mesh(tine, antler, -0.07, 0.34, -0.13, -0.6, 0.2),
      mesh(tine, antler, 0.07, 0.34, -0.13, -0.6, -0.2),
    );
    body.add(head);
    const legs = fourLegs(dark, 0.11, -0.28, 0.28, 0.41, 0.07);
    body.add(...legs);
    return { body, head, legs, legLength: 0.41 };
  },
  tatu: (paint) => {
    // Ouriço: bola de espinhos claros e escuros nas costas e focinho fino; enrola e cava como antes.
    const back = paint("#5A4A3C", 0.8);
    const quill = paint("#3E3229", 0.7);
    const tip = paint("#C2AE96", 0.7);
    const skin = paint("#B49A82", 0.6);
    const nose = paint("#2A211B", 0.5);
    // Corpo: elipsoide (semieixos rx, ry, rz) com centro em (0, cy, cz).
    const [rx, ry, rz, cy, cz] = [0.24, 0.17, 0.3, 0.21, 0.03];
    const body = joint(0, 0, 0, mesh(sphere(1, 8, 6), back, 0, cy, cz));
    body.children[0]!.scale.set(rx, ry, rz);
    // Espinhos em fileiras (u: de −1 na frente a 1 atrás; a: ângulo a partir do alto), saindo na
    // normal da superfície e deitados para trás.
    const spike = cone(0.045, 0.17, 4);
    const dir = new Vector3();
    const rows: [number, number[]][] = [
      [-0.45, [-0.9, 0, 0.9]],
      [0.05, [-1.2, -0.4, 0.4, 1.2]],
      [0.55, [-0.9, 0, 0.9]],
      [0.85, [-0.4, 0.4]],
    ];
    let n = 0;
    for (const [u, angles] of rows) {
      for (const a of angles) {
        const s = Math.sqrt(1 - u * u);
        const [x, y, z] = [rx * s * Math.sin(a), ry * s * Math.cos(a), rz * u];
        dir.set(x / rx ** 2, y / ry ** 2, z / rz ** 2).normalize();
        dir.z += 0.5;
        dir.normalize();
        const m = mesh(spike, n++ % 2 ? tip : quill, x + dir.x * 0.06, cy + y + dir.y * 0.06, cz + z + dir.z * 0.06);
        m.quaternion.setFromUnitVectors(UP, dir);
        body.add(m);
      }
    }
    const dot = box(0.035, 0.035, 0.035);
    const head = joint(0, 0.16, -0.3, mesh(cone(0.09, 0.22, 6), skin, 0, 0, -0.04, -Math.PI / 2));
    head.add(mesh(dot, nose, 0, 0, -0.15), mesh(dot, nose, -0.045, 0.035, -0.05), mesh(dot, nose, 0.045, 0.035, -0.05));
    body.add(head);
    const legs = fourLegs(skin, 0.13, -0.16, 0.16, 0.12, 0.07);
    body.add(...legs);
    return { body, head, legs, legLength: 0.12 };
  },
  ema: (paint) => {
    // Javali: quadrúpede atarracado de cerdas escuras, crista no lombo, focinho chato e duas presas.
    // As quatro patas usam o mesmo rig dos quadrúpedes; o golpe ("chifrada") ergue a pata 1 como o
    // chute antigo, com o corpo empinando.
    const hide = paint("#5B4B3E", 0.8);
    const dark = paint("#2E2620", 0.6);
    const snout = paint("#8A6A5C", 0.6);
    const tusk = paint("#E8DCC0", 0.6);
    const body = joint(0, 0, 0, mesh(box(0.56, 0.56, 0.5), hide, 0, 0.7, -0.2), mesh(box(0.48, 0.46, 0.5), hide, 0, 0.66, 0.26), mesh(box(0.12, 0.1, 0.5), dark, 0, 1.0, -0.18));
    // Cabeça em cunha: o focinho comprido desce um pouco e acaba no disco chato.
    const head = joint(0, 0.74, -0.45, mesh(box(0.32, 0.32, 0.3), hide, 0, 0, -0.1), mesh(box(0.22, 0.17, 0.3), hide, 0, -0.08, -0.36, -0.15), mesh(box(0.18, 0.14, 0.03), snout, 0, -0.105, -0.52, -0.15));
    const fang = cone(0.035, 0.22, 4);
    const ear = cone(0.06, 0.14, 4);
    const eye = box(0.03, 0.03, 0.02);
    head.add(
      mesh(fang, tusk, -0.13, -0.04, -0.44, -0.3, 0.45),
      mesh(fang, tusk, 0.13, -0.04, -0.44, -0.3, -0.45),
      mesh(ear, dark, -0.12, 0.2, -0.04, 0.3),
      mesh(ear, dark, 0.12, 0.2, -0.04, 0.3),
      mesh(eye, dark, -0.1, 0.06, -0.26),
      mesh(eye, dark, 0.1, 0.06, -0.26),
    );
    body.add(head, mesh(cone(0.03, 0.22, 4), dark, 0, 0.78, 0.55, Math.PI / 2 + 0.6));
    const legs = fourLegs(dark, 0.17, -0.3, 0.32, 0.44, 0.11);
    body.add(...legs);
    return { body, head, legs, legLength: 0.44 };
  },
  "corpo-seco": (paint) => {
    // Esqueleto: ossos claros e finos, costelas sobre o vão escuro, caveira de órbitas fundas e olhos de brasa.
    // O osso na cor dos esqueletos KayKit (F50): a troca para o modelo animado não muda o tom.
    const bone = paint(SKELETON_BONE, 0.9);
    const dark = paint("#2A2520", 0.6);
    const eye = paint("#FFB347", 1, true);
    // Coluna da bacia ao pescoço (passa por dentro do vão das costelas).
    const body = joint(0, 0, 0, mesh(box(0.26, 0.34, 0.14), dark, 0, 1.36, 0), mesh(box(0.06, 0.66, 0.06), bone, 0, 1.29, -0.02), mesh(box(0.28, 0.1, 0.14), bone, 0, 0.96, 0));
    const rib = box(0.36, 0.05, 0.2);
    for (const y of [1.5, 1.38, 1.26]) body.add(mesh(rib, bone, 0, y, 0));
    const head = joint(0, 1.58, -0.12, mesh(box(0.26, 0.22, 0.26), bone, 0, 0.17, -0.02), mesh(box(0.18, 0.07, 0.18), bone, 0, 0.03, -0.05));
    const socket = box(0.08, 0.07, 0.02);
    head.add(
      mesh(socket, dark, -0.065, 0.17, -0.155),
      mesh(socket, dark, 0.065, 0.17, -0.155),
      mesh(socket, dark, 0, 0.09, -0.155),
      mesh(box(0.035, 0.035, 0.02), eye, -0.065, 0.17, -0.165),
      mesh(box(0.035, 0.035, 0.02), eye, 0.065, 0.17, -0.165),
    );
    const legs = twoLegs(bone, 0.1, 0.95, 0.06);
    const arms = twoArms(bone, 0.22, 1.5, 0.8, 0.05, -0.08);
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 0.95 };
  },
  lobisomem: (paint) => {
    // Lobo grande e escuro, de pé nas quatro patas, com os olhos vermelhos do modelo animado (a célula 29
    // da raposa no `CREATURE_LOOKS`): a troca não muda a cor deles.
    const fur = paint("#3B3A40", 0.9);
    const dark = paint("#24232A", 0.7);
    const eye = paint(lookHex("lobisomem", 29, "#E8202A"), 1, true);
    const body = joint(0, 0, 0, mesh(box(0.55, 0.6, 1.1), fur, 0, 0.95, 0));
    body.add(mesh(box(0.6, 0.5, 0.5), fur, 0, 1.15, -0.35));
    const head = joint(0, 1.2, -0.62, mesh(box(0.34, 0.32, 0.34), fur, 0, 0, -0.12), mesh(box(0.2, 0.16, 0.3), dark, 0, -0.06, -0.38));
    head.add(
      mesh(cone(0.07, 0.2, 4), fur, -0.11, 0.24, -0.05),
      mesh(cone(0.07, 0.2, 4), fur, 0.11, 0.24, -0.05),
      mesh(box(0.06, 0.04, 0.02), eye, -0.09, 0.06, -0.3),
      mesh(box(0.06, 0.04, 0.02), eye, 0.09, 0.06, -0.3),
    );
    body.add(head, mesh(cone(0.08, 0.5, 5), dark, 0, 1.0, 0.75, Math.PI / 2 + 0.4));
    const legs = fourLegs(dark, 0.2, -0.38, 0.38, 0.7, 0.13);
    body.add(...legs);
    return { body, head, legs, legLength: 0.7 };
  },
  "mula-sem-cabeca": (paint) => {
    // Pesadelo: cavalo negro com cabeça e olhos de brasa; a crina e a cauda são fogo.
    const hide = paint("#221C22", 0.9);
    const hoof = paint("#100D10", 0.6);
    const fire = paint("#FF7A1A", 1, true);
    const flame = paint("#FFD25A", 1, true);
    const body = joint(0, 0, 0, mesh(box(0.55, 0.55, 1.25), hide, 0, 1.05, 0));
    // Pescoço inclinado para a frente e a cabeça comprida apontando para baixo, no pivô do pescoço.
    const head = joint(0, 1.25, -0.6, mesh(box(0.24, 0.62, 0.3), hide, 0, 0.25, -0.14, -0.55), mesh(box(0.22, 0.24, 0.5), hide, 0, 0.45, -0.5, -0.45));
    const ear = cone(0.045, 0.16, 4);
    const eye = box(0.03, 0.05, 0.07);
    head.add(mesh(ear, hide, -0.07, 0.72, -0.33, 0.2), mesh(ear, hide, 0.07, 0.72, -0.33, 0.2), mesh(eye, flame, -0.11, 0.54, -0.43, -0.45), mesh(eye, flame, 0.11, 0.54, -0.43, -0.45));
    // Crina de fogo ao longo da nuca, labaredas inclinadas para trás (laranja e amarela alternadas).
    const blaze = cone(0.1, 0.36, 5);
    for (const [t, big] of [[-0.18, true], [-0.07, false], [0.04, true], [0.15, false], [0.26, true]] as const) {
      head.add(mesh(big ? blaze : cone(0.07, 0.32, 5), big ? fire : flame, 0, 0.44 + 0.852 * t, 0.05 - 0.523 * t, 0.5));
    }
    // A cauda é a mesma labareda, subindo para trás.
    body.add(head, mesh(blaze, fire, 0, 1.16, 0.74, 1.07));
    const legs = fourLegs(hoof, 0.2, -0.45, 0.45, 0.8, 0.12);
    body.add(...legs);
    return { body, head, legs, legLength: 0.8 };
  },
  "matinta-pereira": (paint) => {
    // Pássaro escuro de asas largas (rasga-mortalha), bico claro.
    const feathers = paint("#2B2733", 1);
    const wingMat = paint("#3A3445", 0.9);
    const beak = paint("#C9B48A", 0.6);
    const eye = paint("#E9F2FF", 1, true);
    const body = joint(0, 0, 0, mesh(sphere(0.26, 7, 5), feathers, 0, 0.45, 0));
    (body.children[0] as Mesh).scale.set(0.9, 0.8, 1.4);
    const head = joint(0, 0.55, -0.32, mesh(sphere(0.14, 6, 5), feathers, 0, 0, 0));
    head.add(mesh(cone(0.05, 0.18, 4), beak, 0, -0.02, -0.18, -Math.PI / 2), mesh(box(0.04, 0.04, 0.02), eye, -0.07, 0.04, -0.11), mesh(box(0.04, 0.04, 0.02), eye, 0.07, 0.04, -0.11));
    const wing = box(0.7, 0.04, 0.42);
    const wings = [-1, 1].map((side) => {
      const w = joint(side * 0.18, 0.5, 0, mesh(wing, wingMat, side * 0.36, 0, 0.05));
      return w;
    });
    const legs = twoLegs(beak, 0.07, 0.25, 0.04, 0.05);
    body.add(head, ...wings, ...legs, mesh(box(0.22, 0.03, 0.3), wingMat, 0, 0.42, 0.42));
    return { body, head, legs, wings, legLength: 0.25 };
  },
  cuca: (paint) => {
    // Bruxa: rosto cinza-esverdeado de nariz comprido, cabelo escuro e chapéu pontudo, de vestido.
    // Manto e chapéu nas cores do modelo animado (F50, `CREATURE_LOOKS`).
    const skin = paint(lookHex("cuca", 0, "#A7B597"), 0.8);
    const dress = paint(lookHex("cuca", 8, "#5B3A5E"), 0.8);
    const hat = paint(lookHex("cuca", 9, "#2A2233"), 0.9);
    const hair = paint("#3A3340", 0.8);
    const eye = paint("#FFE066", 1, true);
    const body = joint(0, 0, 0, mesh(cone(0.42, 1.1, 7), dress, 0, 0.75, 0), mesh(box(0.34, 0.4, 0.24), dress, 0, 1.4, 0));
    const head = joint(0, 1.65, -0.02, mesh(box(0.24, 0.26, 0.24), skin, 0, 0.1, 0));
    head.add(
      mesh(cone(0.04, 0.14, 4), skin, 0, 0.07, -0.17, -Math.PI / 2 - 0.3),
      mesh(box(0.3, 0.24, 0.1), hair, 0, 0.06, 0.1),
      mesh(box(0.05, 0.04, 0.02), eye, -0.06, 0.13, -0.125),
      mesh(box(0.05, 0.04, 0.02), eye, 0.06, 0.13, -0.125),
      // Chapéu: aba larga e cone pontudo, com a ponta caindo um pouco para trás.
      mesh(cylinder(0.3, 0.3, 0.03, 10), hat, 0, 0.23, 0),
      mesh(cone(0.15, 0.44, 7), hat, 0, 0.45, 0.03, 0.18),
    );
    const legs = twoLegs(hat, 0.1, 0.3, 0.08);
    const arms = twoArms(skin, 0.23, 1.55, 0.65, 0.08);
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 0.3 };
  },
  // Troll: gigante peludo de cara larga, queixo para a frente, duas presas e braços enormes.
  capelobo: trollBuilder(TROLL),
  mapinguari: (paint) => {
    // Ciclope: gigante de pelo ruivo com um olho só, grande e aceso, sob a sobrancelha no meio da testa.
    const fur = paint("#7A3E22", 1);
    const dark = paint("#3F2114", 0.7);
    const mouth = paint("#2A0E08", 0.4);
    const white = paint("#E8DCC0", 0.6);
    const eye = paint("#FF4B2B", 1, true);
    const body = joint(0, 0, 0, mesh(box(1.5, 1.7, 1.0), fur, 0, 2.15, 0), mesh(box(1.2, 0.6, 0.85), fur, 0, 1.15, 0));
    const head = joint(0, 3.0, -0.05, mesh(box(0.8, 0.6, 0.7), fur, 0, 0.25, 0));
    head.add(mesh(sphere(0.2, 8, 6), white, 0, 0.32, -0.3), mesh(sphere(0.1, 7, 5), eye, 0, 0.32, -0.45), mesh(box(0.66, 0.1, 0.14), dark, 0, 0.53, -0.36), mesh(box(0.34, 0.07, 0.04), mouth, 0, 0.07, -0.36));
    const legs = twoLegs(dark, 0.42, 0.95, 0.36);
    const arms = twoArms(fur, 0.95, 2.85, 1.9, 0.34);
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 0.95 };
  },
  "cabra-cabriola": (paint) => {
    // Cabra enorme e escura, chifres retorcidos, fogo nos olhos e na boca.
    const hide = paint("#2F2A26", 0.9);
    const horn = paint("#CDBB95", 0.6);
    const hoof = paint("#14110F", 0.6);
    const fire = paint("#FF6A1A", 1, true);
    const body = joint(0, 0, 0, mesh(box(0.75, 0.8, 1.55), hide, 0, 1.35, 0));
    const head = joint(0, 1.65, -0.8, mesh(box(0.42, 0.42, 0.6), hide, 0, 0.1, -0.15));
    head.add(
      mesh(cone(0.09, 0.6, 5), horn, -0.18, 0.5, 0.05, -0.5),
      mesh(cone(0.09, 0.6, 5), horn, 0.18, 0.5, 0.05, -0.5),
      mesh(box(0.08, 0.06, 0.02), fire, -0.12, 0.18, -0.46),
      mesh(box(0.08, 0.06, 0.02), fire, 0.12, 0.18, -0.46),
      mesh(cone(0.1, 0.3, 5), fire, 0, -0.05, -0.58, -Math.PI / 2),
      mesh(box(0.14, 0.2, 0.1), hide, 0, -0.18, -0.3),
    );
    body.add(head, mesh(cone(0.07, 0.35, 4), hide, 0, 1.6, 0.85, Math.PI / 2 - 0.4));
    const legs = fourLegs(hoof, 0.27, -0.58, 0.58, 0.95, 0.15);
    body.add(...legs);
    return { body, head, legs, legLength: 0.95 };
  },
  "cuca-rainha": (paint) => {
    // Rainha-Bruxa: a Bruxa maior, de manto, cabelo prateado e chapéu mais alto com coroa de ouro.
    const skin = paint(lookHex("cuca-rainha", 0, "#A7B597"), 0.9);
    const cloak = paint(lookHex("cuca-rainha", 8, "#3D1F4A"), 0.9);
    const hat = paint(lookHex("cuca-rainha", 9, "#2A2233"), 0.9);
    const gold = paint("#E2B23A", 1.1);
    const hair = paint("#C9C3D6", 1);
    const eye = paint("#FFE066", 1, true);
    const body = joint(0, 0, 0, mesh(cone(0.65, 1.55, 8), cloak, 0, 0.95, 0), mesh(box(0.5, 0.55, 0.34), cloak, 0, 1.85, 0));
    const head = joint(0, 2.2, -0.02, mesh(box(0.32, 0.34, 0.32), skin, 0, 0.13, 0));
    head.add(
      mesh(cone(0.05, 0.18, 4), skin, 0, 0.09, -0.22, -Math.PI / 2 - 0.3),
      mesh(box(0.4, 0.32, 0.12), hair, 0, 0.08, 0.13),
      mesh(box(0.07, 0.05, 0.02), eye, -0.08, 0.17, -0.165),
      mesh(box(0.07, 0.05, 0.02), eye, 0.08, 0.17, -0.165),
      mesh(cylinder(0.4, 0.4, 0.04, 10), hat, 0, 0.3, 0),
      mesh(cone(0.2, 0.72, 8), hat, 0, 0.66, 0.04, 0.15),
      // Coroa: faixa de ouro na base do chapéu, com pontas em volta.
      mesh(cylinder(0.19, 0.21, 0.12, 8), gold, 0, 0.38, 0.01),
    );
    head.add(...crownPoints(gold, 0.19, 0.5, 0.01));
    const legs = twoLegs(hat, 0.14, 0.35, 0.1);
    const arms = twoArms(skin, 0.32, 2.05, 0.9, 0.1);
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 0.35 };
  },
  "corpo-seco-anciao": (paint) => {
    // Rei Esqueleto: o Esqueleto grande, de manto real, caveira coroada e olhos verdes.
    const bone = paint(SKELETON_BONE, 1);
    const dark = paint("#221D19", 0.6);
    const mantle = paint("#5A1F2E", 0.9);
    const gold = paint("#E2B23A", 1.1);
    const eye = paint("#9BFF6A", 1, true);
    const body = joint(0, 0, 0, mesh(box(0.46, 0.6, 0.24), dark, 0, 2.1, 0), mesh(box(0.1, 0.42, 0.1), bone, 0, 1.6, 0.03), mesh(box(0.46, 0.16, 0.24), bone, 0, 1.42, 0));
    const rib = box(0.6, 0.08, 0.32);
    for (const y of [2.3, 2.14, 1.98]) body.add(mesh(rib, bone, 0, y, 0));
    // Manto: gola sobre os ombros e a capa caindo pelas costas.
    body.add(mesh(box(0.76, 0.2, 0.46), mantle, 0, 2.46, 0.04), mesh(box(0.72, 1.1, 0.06), mantle, 0, 1.92, 0.25));
    const head = joint(0, 2.6, -0.25, mesh(box(0.4, 0.34, 0.4), bone, 0, 0.24, -0.02), mesh(box(0.28, 0.1, 0.28), bone, 0, 0.03, -0.06));
    const socket = box(0.11, 0.1, 0.02);
    head.add(
      mesh(socket, dark, -0.1, 0.26, -0.225),
      mesh(socket, dark, 0.1, 0.26, -0.225),
      mesh(box(0.06, 0.07, 0.02), dark, 0, 0.13, -0.225),
      mesh(box(0.06, 0.05, 0.02), eye, -0.1, 0.26, -0.24),
      mesh(box(0.06, 0.05, 0.02), eye, 0.1, 0.26, -0.24),
      mesh(cylinder(0.24, 0.24, 0.1, 8), gold, 0, 0.46, -0.02),
      ...crownPoints(gold, 0.2, 0.58, -0.02),
    );
    const legs = twoLegs(bone, 0.18, 1.4, 0.1);
    for (const leg of legs) leg.add(mesh(box(0.14, 0.06, 0.22), bone, 0, -1.37, -0.05));
    const arms = twoArms(bone, 0.42, 2.35, 1.5, 0.09, -0.1);
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 1.4 };
  },
  boitata: (paint) => {
    // Serpente de fogo (doc 00): cabeça de olhos acesos e o corpo em anéis que esfriam até a cauda.
    const fire = paint("#FF6A1A", 1, true);
    const flame = paint("#FFD25A", 1, true);
    const ember = paint("#C2361A", 1, true);
    const eye = paint("#FFF6D0", 1, true);
    const body = joint(0, 0, 0);
    const head = joint(0, 1.6, -0.4, mesh(box(1.2, 0.9, 1.6), fire, 0, 0, -0.4), mesh(cone(0.5, 1.0, 5), flame, 0, 0.1, -1.5, -Math.PI / 2));
    head.add(mesh(box(0.18, 0.12, 0.04), eye, -0.3, 0.3, -1.2), mesh(box(0.18, 0.12, 0.04), eye, 0.3, 0.3, -1.2));
    body.add(head);
    for (let i = 0; i < 12; i++) {
      const r = 0.7 - i * 0.04;
      const t = i / 11;
      body.add(mesh(sphere(r, 7, 5), i % 3 === 0 ? flame : i % 3 === 1 ? fire : ember, Math.sin(t * Math.PI * 2) * 1.6, 0.9 - t * 0.6, 0.4 + i * 0.85));
    }
    return { body, head, legs: [], legLength: 0.5 };
  },
  // Inimigos maiores (F89): o boneco de antes com as medidas multiplicadas (a reserva procedural deles; o
  // Troll de Pedra também é a pele rígida do modelo animado, com as peças do kit do Blender).
  "lobisomem-alfa": scaled("lobisomem", 1.55),
  // Do Esqueleto comum (não do Rei: a coroa diria chefe), 1,75×.
  "corpo-seco-colosso": scaled("corpo-seco", 1.75),
  "capelobo-ancestral": scaled(trollBuilder({ fur: STONE, dark: STONE_DARK, face: STONE, tusk: TROLL.tusk, eye: TROLL.eye }), 1.35),
  "guardiao-verde": guardianBuilder("#3FAF5A"),
  "guardiao-azul": guardianBuilder("#3A8FD9"),
  "guardiao-vermelho": guardianBuilder("#D9442E"),
  "guardiao-amarelo": guardianBuilder("#F2C230"),
  "guardiao-rosa": guardianBuilder("#F07AB8"),
};

/** Cores do Troll: pelo (ou pedra), o escuro, o rosto, as presas e os olhos acesos. */
interface TrollColors {
  fur: string;
  dark: string;
  face: string;
  tusk: string;
  eye: string;
}

/** O boneco do Troll nas cores dadas (o Troll de antes e, maior, o Troll de Pedra da F89). */
function trollBuilder(colors: TrollColors): Builder {
  return (paint) => {
    const fur = paint(colors.fur, 1);
    const dark = paint(colors.dark, 0.7);
    const face = paint(colors.face, 0.7);
    const tusk = paint(colors.tusk, 0.6);
    const eye = paint(colors.eye, 1, true);
    const body = joint(0, 0, 0, mesh(box(0.95, 1.05, 0.65), fur, 0, 1.55, 0), mesh(box(0.75, 0.5, 0.55), fur, 0, 0.95, 0));
    const head = joint(0, 2.15, -0.15, mesh(box(0.46, 0.4, 0.44), fur, 0, 0.1, -0.05));
    // Queixo largo saltado para a frente, sobrancelha pesada e as presas subindo do queixo.
    const fang = cone(0.06, 0.3, 4);
    head.add(
      mesh(box(0.56, 0.2, 0.3), face, 0, -0.08, -0.2),
      mesh(box(0.46, 0.07, 0.08), dark, 0, 0.22, -0.28),
      mesh(fang, tusk, -0.2, 0.12, -0.33, 0, 0.25),
      mesh(fang, tusk, 0.2, 0.12, -0.33, 0, -0.25),
      mesh(box(0.07, 0.05, 0.02), eye, -0.12, 0.16, -0.28),
      mesh(box(0.07, 0.05, 0.02), eye, 0.12, 0.16, -0.28),
    );
    const legs = twoLegs(dark, 0.22, 0.75, 0.2);
    const arms = twoArms(fur, 0.6, 1.95, 1.25, 0.22);
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 0.75 };
  };
}

/**
 * O boneco de `base` (o nome de outro tipo ou um construtor) com todas as medidas multiplicadas por `k`
 * (F89): as posições e as geometrias, refeitas pelos parâmetros delas (caixa, cone, cilindro, esfera).
 * Não usa a escala dos objetos: a animação reescreve a do corpo a cada quadro, e a pele rígida mede os
 * membros pelos parâmetros das caixas.
 */
function scaled(base: string | Builder, k: number): Builder {
  return (paint) => {
    const build = typeof base === "string" ? CREATURE_SHAPES[base]! : base;
    const parts = build(paint);
    parts.body.traverse((object) => {
      object.position.multiplyScalar(k);
      if (object instanceof Mesh) object.geometry = scaledGeometry(object.geometry as BufferGeometry, k);
    });
    return { ...parts, legLength: parts.legLength * k };
  };
}

function scaledGeometry(geometry: BufferGeometry, k: number): BufferGeometry {
  if (geometry instanceof BoxGeometry) {
    const p = geometry.parameters;
    return box(p.width * k, p.height * k, p.depth * k);
  }
  // O cone é um cilindro: vem antes.
  if (geometry instanceof ConeGeometry) {
    const p = geometry.parameters;
    return cone(p.radius * k, p.height * k, p.radialSegments);
  }
  if (geometry instanceof CylinderGeometry) {
    const p = geometry.parameters;
    return cylinder(p.radiusTop * k, p.radiusBottom * k, p.height * k, p.radialSegments, p.thetaStart, p.thetaLength);
  }
  if (geometry instanceof SphereGeometry) {
    const p = geometry.parameters;
    return sphere(p.radius * k, p.widthSegments, p.heightSegments);
  }
  throw new Error(`boneco: geometria sem parâmetros para escalar (${geometry.type})`);
}

/**
 * Guardião dos Muiraquitãs (doc 06): sentinela de pedra encantada, com o cristal do peito, os
 * olhos e as ombreiras na cor do santuário.
 */
function guardianBuilder(color: string): Builder {
  return (paint) => {
    const stone = paint(STONE, 0.8);
    const dark = paint(STONE_DARK, 0.6);
    const accent = paint(color, 1);
    const glow = paint(color, 1, true);
    const body = joint(0, 0, 0, mesh(box(0.9, 1.0, 0.55), stone, 0, 1.6, 0), mesh(box(0.6, 0.4, 0.45), stone, 0, 0.98, 0));
    body.add(mesh(cone(0.16, 0.36, 4), glow, 0, 1.7, -0.3, -Math.PI / 2), mesh(box(0.34, 0.16, 0.5), accent, -0.6, 2.05, 0), mesh(box(0.34, 0.16, 0.5), accent, 0.6, 2.05, 0));
    const head = joint(0, 2.15, -0.02, mesh(box(0.42, 0.38, 0.4), stone, 0, 0.18, 0));
    head.add(mesh(box(0.08, 0.05, 0.02), glow, -0.1, 0.22, -0.21), mesh(box(0.08, 0.05, 0.02), glow, 0.1, 0.22, -0.21), mesh(cone(0.1, 0.22, 4), accent, 0, 0.48, 0));
    const legs = twoLegs(dark, 0.2, 0.8, 0.2);
    const arms = twoArms(stone, 0.58, 2.0, 1.15, 0.2);
    for (const arm of arms) arm.add(mesh(box(0.28, 0.28, 0.28), dark, 0, -1.2, 0));
    body.add(head, ...legs, ...arms);
    return { body, head, legs, arms, legLength: 0.8 };
  };
}
